import i18n from '../i18n';
import axios from 'axios';

// Translation cache to avoid repeated API calls
const translationCache = new Map();

// Cache key generator
const getCacheKey = (text, targetLang) => {
  return `${text}_${targetLang}`;
};

// Check if text is likely in English (simple heuristic)
const isLikelyEnglish = (text) => {
  if (!text || typeof text !== 'string') return true;
  
  // Check for common English words/patterns
  const englishPattern = /^[a-zA-Z0-9\s.,!?'"()-]+$/;
  const hasEnglishChars = englishPattern.test(text);
  
  // Check for common Indian language scripts
  const kannadaPattern = /[\u0C80-\u0CFF]/;
  const tamilPattern = /[\u0B80-\u0BFF]/;
  const teluguPattern = /[\u0C00-\u0C7F]/;
  const malayalamPattern = /[\u0D00-\u0D7F]/;
  const hindiPattern = /[\u0900-\u097F]/;
  const marathiPattern = /[\u0900-\u097F]/;
  
  const hasIndianScript = kannadaPattern.test(text) || 
                         tamilPattern.test(text) || 
                         teluguPattern.test(text) || 
                         malayalamPattern.test(text) || 
                         hindiPattern.test(text) || 
                         marathiPattern.test(text);
  
  // If it has Indian script, it's not English
  if (hasIndianScript) return false;
  
  // If it only has English characters, assume it's English
  return hasEnglishChars;
};

/**
 * Detects the language of the text
 * @param {string} text - Text to detect language for
 * @returns {string} - Language code (en, kn, ta, etc.)
 */
const detectLanguage = (text) => {
  if (!text || typeof text !== 'string') return 'en';
  
  // Check for Kannada script
  if (/[\u0C80-\u0CFF]/.test(text)) return 'kn';
  // Check for Tamil script
  if (/[\u0B80-\u0BFF]/.test(text)) return 'ta';
  // Check for Telugu script
  if (/[\u0C00-\u0C7F]/.test(text)) return 'te';
  // Check for Malayalam script
  if (/[\u0D00-\u0D7F]/.test(text)) return 'ml';
  // Check for Hindi/Marathi script (Devanagari)
  if (/[\u0900-\u097F]/.test(text)) {
    // Could be Hindi or Marathi - default to Hindi
    return 'hi';
  }
  
  // Default to English if no Indian script detected
  return 'en';
};

/**
 * Translates user-entered text to the current preferred language
 * @param {string} text - Text to translate
 * @param {string} targetLang - Target language code (optional, defaults to current i18n language)
 * @returns {Promise<string>} - Translated text or original if translation fails/not needed
 */
export const translateText = async (text, targetLang = null) => {
  // Return empty/null values as-is
  if (!text || typeof text !== 'string' || text.trim() === '') {
    return text;
  }

  const trimmedText = text.trim();
  const currentLang = targetLang || i18n.language || 'en';
  
  // If target language is English, return as-is (no translation needed)
  if (currentLang === 'en') {
    return trimmedText;
  }

  // Check cache first
  const cacheKey = getCacheKey(trimmedText, currentLang);
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }

  // Check localStorage cache
  const storedCache = localStorage.getItem('translationCache');
  if (storedCache) {
    try {
      const cache = JSON.parse(storedCache);
      if (cache[cacheKey]) {
        translationCache.set(cacheKey, cache[cacheKey]);
        return cache[cacheKey];
      }
    } catch (e) {
      // Invalid cache, ignore
    }
  }

  // Detect source language
  const sourceLang = detectLanguage(trimmedText);
  
  // If source language matches target language, no translation needed
  if (sourceLang === currentLang) {
    translationCache.set(cacheKey, trimmedText);
    return trimmedText;
  }

  // If source is English and target is not, or vice versa, translate
  // Also translate between Indian languages
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('patientToken');
    const response = await axios.post('/api/translate', {
      text: trimmedText,
      sourceLang: sourceLang,
      targetLang: currentLang
    }, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });

    if (response.data.success && response.data.translatedText) {
      const translated = response.data.translatedText;
      
      // Cache the translation
      translationCache.set(cacheKey, translated);
      
      // Update localStorage cache (keep last 100 translations)
      const storedCache = localStorage.getItem('translationCache');
      let cache = storedCache ? JSON.parse(storedCache) : {};
      cache[cacheKey] = translated;
      
      // Keep only last 100 entries
      const keys = Object.keys(cache);
      if (keys.length > 100) {
        const oldestKeys = keys.slice(0, keys.length - 100);
        oldestKeys.forEach(key => delete cache[key]);
      }
      
      localStorage.setItem('translationCache', JSON.stringify(cache));
      
      return translated;
    }
  } catch (error) {
    console.error('Translation error:', error);
    // On error, return original text
  }

  // If translation fails, return original text
  translationCache.set(cacheKey, trimmedText);
  return trimmedText;
};

/**
 * Translates an array of texts
 * @param {string[]} texts - Array of texts to translate
 * @param {string} targetLang - Target language code
 * @returns {Promise<string[]>} - Array of translated texts
 */
export const translateTextArray = async (texts, targetLang = null) => {
  if (!Array.isArray(texts) || texts.length === 0) {
    return texts;
  }

  const translated = await Promise.all(
    texts.map(text => translateText(text, targetLang))
  );

  return translated;
};

/**
 * Clears the translation cache
 */
export const clearTranslationCache = () => {
  translationCache.clear();
  localStorage.removeItem('translationCache');
};

export default translateText;

